import { formatNumberRu } from '@wit-support/ui';
/**
 * Форматирование сумм раздела. Деньги в контрактах — ДЕСЯТИЧНЫЕ СТРОКИ
 * (`'4791.37'`), не minor-единицы и не float: «без НДС» оператор вводит в
 * портал до цента, поэтому строка не прогоняется через `Number` целиком —
 * дробная часть берётся как есть (с округлением half-up до двух знаков, если
 * платёжка когда-нибудь пришлёт больше), а через `formatNumberRu` идёт только
 * целая часть — ради русских разделителей разрядов.
 */
const DECIMAL_RE = /^(-?)(\d+)(?:\.(\d+))?$/;
const CURRENCY_SIGN = { EUR: '€', GBP: '£', USD: '$' };
/** Знак валюты (`€`), для неизвестной — сам код (`CHF`). */
export function currencySign(currency) {
    return CURRENCY_SIGN[currency.toUpperCase()] ?? currency.toUpperCase();
}
/** Целая часть с разрядами: `'12345'` → «12 345». */
function groupInt(digits) {
    // Строки длиннее 15 цифр (за пределом точности double) не форматируются.
    if (digits.length > 15)
        return digits;
    return formatNumberRu(Number(digits), { maximumFractionDigits: 0 });
}
/** half-up до двух знаков на строках: `'1.005'` → `{ int: '1', frac: '01' }`. */
function round2(int, frac) {
    if (frac.length <= 2)
        return { int, frac: frac.padEnd(2, '0') };
    const head = frac.slice(0, 2);
    if (Number(frac[2]) < 5)
        return { int, frac: head };
    // Перенос: «99» + 1 → «00» и +1 к целой части.
    const cents = Number(head) + 1;
    if (cents < 100)
        return { int, frac: String(cents).padStart(2, '0') };
    return { int: (BigInt(int) + 1n).toString(), frac: '00' };
}
/**
 * Сумма из десятичной строки: «9 840,31». `wholeUnits` — без копеек (box 6
 * UK — целые фунты, платёжка уже отбросила дробь). Не десятичная строка или
 * `null` — «—».
 */
export function formatDecimal(value, opts = {}) {
    if (value == null)
        return '—';
    const m = DECIMAL_RE.exec(value.trim());
    if (!m)
        return '—';
    const [, sign, int = '0', frac = ''] = m;
    if (opts.wholeUnits) {
        const isZero = /^0+$/.test(int);
        return `${sign && !isZero ? '−' : ''}${groupInt(int)}`;
    }
    const r = round2(int, frac);
    const isZero = /^0+$/.test(r.int) && r.frac === '00';
    return `${sign && !isZero ? '−' : ''}${groupInt(r.int)},${r.frac}`;
}
/** Сумма с валютой: «9 840,31 €». */
export function formatMoney(value, currency, opts = {}) {
    const n = formatDecimal(value, opts);
    return n === '—' ? n : `${n} ${currencySign(currency)}`;
}
/**
 * Ставка НДС — платёжка шлёт её В ПРОЦЕНТАХ десятичной строкой (`'21'`,
 * `'25.5'`), не долей: `'25.5'` → «25,5 %»; `null` и мусор — «—».
 */
export function formatRate(rate) {
    if (rate == null)
        return '—';
    const m = DECIMAL_RE.exec(rate.trim());
    if (!m)
        return '—';
    const [, sign, int = '0', frac = ''] = m;
    const f = frac.replace(/0+$/, '');
    return `${sign ? '−' : ''}${groupInt(int)}${f ? `,${f}` : ''} %`;
}
/** Курс: `'1.1355'` → «1,1355» (все знаки платёжки, без округления). */
export function formatFxRate(rate) {
    const m = DECIMAL_RE.exec(rate.trim());
    if (!m)
        return rate;
    const [, sign, int = '0', frac] = m;
    return `${sign ? '−' : ''}${groupInt(int)}${frac ? `,${frac}` : ''}`;
}
/**
 * Точная сумма десятичных строк (BigInt по общему масштабу, без float):
 * `['-29.81', '-7.4']` → `'-37.21'`. Не десятичная строка — пропускается.
 */
export function sumDecimals(values) {
    const parsed = values
        .map((v) => DECIMAL_RE.exec(v.trim()))
        .filter((m) => m !== null);
    const scale = Math.max(0, ...parsed.map((m) => (m[3] ?? '').length));
    let total = 0n;
    for (const [, sign, int = '0', frac = ''] of parsed) {
        const n = BigInt(int + frac.padEnd(scale, '0'));
        total += sign ? -n : n;
    }
    const neg = total < 0n;
    const digits = (neg ? -total : total).toString().padStart(scale + 1, '0');
    const int = digits.slice(0, digits.length - scale);
    const frac = digits.slice(digits.length - scale);
    return `${neg ? '-' : ''}${int}${scale > 0 ? `.${frac}` : ''}`;
}
/** Центы USD со знаком (единственное поле в minor-единицах — строки расчёта). */
export function formatUsdMinor(minor) {
    const sign = minor < 0 ? '-' : '';
    const abs = Math.abs(Math.trunc(minor));
    const int = Math.floor(abs / 100);
    const cents = String(abs % 100).padStart(2, '0');
    return formatMoney(`${sign}${int}.${cents}`, 'USD');
}
/** Сравнение десятичных строк для сортировки таблицы (точности double хватает). */
export function compareDecimal(a, b) {
    const x = a == null ? Number.NEGATIVE_INFINITY : Number(a);
    const y = b == null ? Number.NEGATIVE_INFINITY : Number(b);
    return x === y ? 0 : x < y ? -1 : 1;
}
const QUARTER_RE = /^(\d{4})-Q([1-4])$/;
/**
 * Конец квартала — первый момент СЛЕДУЮЩЕГО квартала по UTC:
 * `'2026-Q3'` → 2026-10-01T00:00:00Z. Не квартал — `null`.
 */
export function quarterEndUtc(quarter) {
    const m = QUARTER_RE.exec(quarter);
    if (!m)
        return null;
    const year = Number(m[1]);
    const q = Number(m[2]);
    return new Date(Date.UTC(year, q * 3, 1));
}
/** `'2026-Q3'` → «3-й квартал 2026». */
export function quarterLabelRu(quarter) {
    const m = QUARTER_RE.exec(quarter);
    if (!m)
        return quarter;
    return `${m[2]}-й квартал ${m[1]}`;
}
/**
 * Кто посчитал / подал (`computedBy`, `filedBy`): `scheduler` — «автоматически»,
 * `admin:<id> <email>` — email, остальное — как есть. Общий хелпер кита —
 * тот же, что у «Контента» и «Соцсетей».
 */
export { actorLabelRu } from '@wit-support/ui-domain';
//# sourceMappingURL=format.js.map